import React from 'react';
import { Form, Input, Modal, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { OrganizationDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { BasicFormWrapper, CustomSwitch } from 'container/styled';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { OrganizationsApi } from 'api/api';

interface OrganizationFormProps {
  visible: boolean;
  editMode: boolean;
  organization?: OrganizationDto | null;
  onCancel: () => void;
  onSuccess: (organizationId: number) => Promise<void>;
}

const organizationsApi = new OrganizationsApi();

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  visible,
  editMode,
  organization,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (organization && editMode) {
      form.setFieldsValue({
        id: organization.id,
        name: organization.name,
        description: organization.description,
        email: organization.email,
        phoneNumber: organization.phoneNumber,
        address: organization.address,
        isActive: organization.isActive,
      });
    }
  }, [organization, editMode, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      if (editMode) {
        await organizationsApi.apiOrganizationsIdPut({
          id: values.id,
          updateOrganizationCommand: values,
        });
      } else {
        await organizationsApi.apiOrganizationsPost({
          createOrganizationCommand: {
            ...values,
            ownerFirstName: values.ownerFirstName,
            ownerLastName: values.ownerLastName,
          },
        });
      }

      openNotificationWithIcon(
        'success',
        t(
          `organizations:${editMode ? 'updated' : 'created'}`,
          `Organization ${editMode ? 'updated' : 'created'} successfully`
        )
      );
      await onSuccess(values.id);
    } catch (err) {
      openNotificationWithIcon('error', t('organizations:error', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t(`organizations:${editMode ? 'edit' : 'create'}`, `${editMode ? 'Edit' : 'Create'} Organization`)}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <BasicFormWrapper>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label={t('organizations:name', 'Name')}
            name="name"
            rules={[
              { required: true, message: t('organizations:name-required', 'Name is required') },
              { max: 200, message: t('organizations:name-max', 'Name cannot exceed 200 characters') }
            ]}
          >
            <Input prefix={<FeatherIcon icon="user" size={16} />} />
          </Form.Item>

          <Form.Item
            label={t('organizations:description', 'Description')}
            name="description"
            rules={[
              { max: 500, message: t('organizations:description-max', 'Description cannot exceed 500 characters') }
            ]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            label={t('organizations:email', 'Email')}
            name="email"
            rules={[
              { required: true, message: t('organizations:email-required', 'Email is required') },
              { type: 'email', message: t('organizations:email-invalid', 'Invalid email format') }
            ]}
          >
            <Input prefix={<FeatherIcon icon="mail" size={16} />} />
          </Form.Item>

          <Form.Item
            label={t('organizations:phone', 'Phone Number')}
            name="phoneNumber"
            rules={[
              { 
                pattern: /^\+?[\d\s-]+$/,
                message: t('organizations:phone-invalid', 'Invalid phone number format')
              }
            ]}
          >
            <Input prefix={<FeatherIcon icon="phone" size={16} />} />
          </Form.Item>

          <Form.Item
            label={t('organizations:createress', 'Address')}
            name="address"
            rules={[
              { max: 500, message: t('organizations:createress-max', 'Address cannot exceed 500 characters') }
            ]}
          >
            <Input prefix={<FeatherIcon icon="map-pin" size={16} />} />
          </Form.Item>

          {!editMode && (
            <>
              <Form.Item
                label={t('organizations:owner-first-name', "Owner's First Name")}
                name="ownerFirstName"
                rules={[
                  { required: true, message: t('organizations:owner-first-name-required', "Owner's first name is required") }
                ]}
              >
                <Input prefix={<FeatherIcon icon="user" size={16} />} />
              </Form.Item>

              <Form.Item
                label={t('organizations:owner-last-name', "Owner's Last Name")}
                name="ownerLastName"
                rules={[
                  { required: true, message: t('organizations:owner-last-name-required', "Owner's last name is required") }
                ]}
              >
                <Input prefix={<FeatherIcon icon="user" size={16} />} />
              </Form.Item>
            </>
          )}

          <Form.Item
            label={t('organizations:status', 'Status')}
            name="isActive"
            valuePropName="checked"
          >
            <CustomSwitch />
          </Form.Item>

          <div className="form-actions">
            <Button onClick={onCancel} style={{ marginRight: 8 }}>
              {t('global:cancel', 'Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editMode 
                ? t('global:update', 'Update')
                : t('global:create', 'Create')
              }
            </Button>
          </div>
        </Form>
      </BasicFormWrapper>
    </Modal>
  );
};